import type {
  SessionPayload,
  WebcheckinDocumentType,
  WebcheckinOccupant,
} from "../types";

const API_BASE = import.meta.env.VITE_WEBCHECKIN_API_URL ||
  "https://webcheckin-api-853794957169.europe-west3.run.app";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /** Optional structured body — e.g. `{ step, missing }` on a 412 from submit. */
    public readonly body?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
  get isExpired() { return this.status === 401 || this.status === 410; }
  /** Wizard step the server suggests routing to (from a 412 payload). */
  get step(): string | null {
    const s = this.body?.step;
    return typeof s === "string" ? s : null;
  }
}

function endpoint(token: string, path = ""): string {
  return `${API_BASE}/api/v1/public/webcheckin/${encodeURIComponent(token)}${path}`;
}

async function call<T>(url: string, init: RequestInit = {}): Promise<T> {
  const resp = await fetch(url, {
    ...init,
    headers: {
      Accept: "application/json",
      ...(init.body && !(init.body instanceof FormData) ? { "Content-Type": "application/json" } : {}),
      ...init.headers,
    },
  });
  if (!resp.ok) {
    let message = `HTTP ${resp.status}`;
    let body: Record<string, unknown> | undefined;
    try {
      body = await resp.json();
      if (body && typeof body.error === "string") message = body.error;
    } catch {
      // non-JSON error body — keep the default message
    }
    throw new ApiError(resp.status, message, body);
  }
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

export function getSession(token: string): Promise<SessionPayload> {
  return call<SessionPayload>(endpoint(token));
}


export interface OccupantPatch {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string | null;
  nationality?: string | null;
  documentType?: WebcheckinDocumentType | null;
  documentNumber?: string | null;
  phoneE164?: string | null;
  email?: string | null;
}

export function updateOccupant(
  token: string,
  occupantId: string,
  patch: OccupantPatch
): Promise<{ occupant: WebcheckinOccupant }> {
  return call(endpoint(token, `/occupants/${encodeURIComponent(occupantId)}`), {
    method: "PUT",
    body: JSON.stringify(patch),
  });
}

export function deleteDocument(
  token: string,
  occupantId: string,
  side: "front" | "back" | "both" = "both"
): Promise<{ ok: true; removed: { front: boolean; back: boolean } }> {
  const qs = new URLSearchParams({ occupant_id: occupantId, side }).toString();
  return call(endpoint(token, `/document?${qs}`), { method: "DELETE" });
}

export function setEta(
  token: string,
  etaTime: string | null,
  specialRequests: string | null
): Promise<{ ok: true }> {
  return call(endpoint(token, "/eta"), {
    method: "PUT",
    body: JSON.stringify({ etaTime, specialRequests }),
  });
}

export interface ConsentBody {
  tncAccepted: boolean;
  houseRulesAccepted: boolean;
  gdprAccepted: boolean;
  marketingOptIn: boolean;
  /** «No emails after my stay». */
  emailOptOut?: boolean;
  signaturePngBase64: string;
}

export function setConsent(token: string, body: ConsentBody): Promise<{ ok: true }> {
  return call(endpoint(token, "/consent"), {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export function submit(token: string): Promise<{
  ok: true;
  submittedAt: string;
  propertyName: string;
  arrivalDate: string;
}> {
  return call(endpoint(token, "/submit"), {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function getArrivalInfo(token: string): Promise<{
  arrivalInstructionsHtml: string | null;
  propertyName: string | null;
  propertyPhone: string | null;
  hotelAppUrl: string | null;
}> {
  return call(endpoint(token, "/arrival-info"));
}
